import { useQuery } from "@tanstack/solid-query";
import type { TovDefinitionEvidenceResponse } from "contracts/api/client-tov/evidence.responses";
import type { TovProfileResponse } from "contracts/api/client-tov/responses";
import type { TovSourceResponse } from "contracts/api/client-tov/sources.responses";
import type { TovVersionResponse } from "contracts/api/client-tov/version.responses";
import { createEffect, createMemo, createSignal, onCleanup } from "solid-js";
import type { BrandTab } from "../../enums/scan";
import { BRAND_TAB } from "../../enums/scan";
import { KEYBOARD_SCOPE } from "../../enums/shell";
import { TOV_SOURCE_STATE } from "../../enums/tov";
import { setScope } from "../../keyboard/manager";
import { api } from "../../store/api";
import { SOURCE_DETAIL_POLL_MS } from "../../store/pollCadence";
import {
  coverageRows,
  definitionCounts,
  emptyCounts,
  sourceCoverageState,
  sourceTrust,
  traceabilityState,
} from "../tov/guidelineCoverage";
import {
  type GuidelineCard,
  guidelineCards,
  type SourceDetail,
} from "../tov/guidelineModel";
import { isCascadeActive } from "../tov/statusLabels";
import { createMapAttemptStore } from "../tov/useMapAttempt";
import { createWebsiteStore } from "../website/store";
import { createTovStore } from "../website/tovStore";

export type EvidenceSelection = {
  item: GuidelineCard;
  link: TovDefinitionEvidenceResponse;
  source: TovSourceResponse | null;
  profile: TovProfileResponse;
  version: TovVersionResponse;
};
export const TABS: Array<{ key: BrandTab; label: string }> = [
  { key: BRAND_TAB.OVERVIEW, label: "Overview" },
  { key: BRAND_TAB.VOICE, label: "Voice & Tone" },
  { key: BRAND_TAB.STRATEGY, label: "Strategy" },
  { key: BRAND_TAB.CHANNELS, label: "Channels" },
  { key: BRAND_TAB.SOURCES, label: "Sources" },
  { key: BRAND_TAB.ACTIVITY, label: "Activity" },
];

export function useBrandGuidelines(props: { websiteId: string }) {
  setScope(KEYBOARD_SCOPE.LIST);
  const context = createWebsiteStore(props.websiteId);
  const clientId = () => context.data()?.site.clientId ?? undefined;
  const [activeTab, setActiveTab] = createSignal<BrandTab>(BRAND_TAB.OVERVIEW);
  const [selectedEvidence, setSelectedEvidence] =
    createSignal<EvidenceSelection | null>(null);
  const tov = createTovStore(clientId);
  const activeProfile = tov.activeProfile;
  const version = tov.version;
  const sourceList = tov.sources;
  const hasActiveVersion = tov.hasActiveVersion;
  const tovVersionId = tov.versionId;
  const mapAttempt = createMapAttemptStore(clientId, tov.versionId);
  const sourceDetailsQuery = useQuery(() => ({
    queryKey: [
      "tov-source-details",
      clientId(),
      (sourceList() ?? []).map((source) => source.id),
    ],
    queryFn: async () => {
      const currentClientId = clientId() as string;
      return Promise.all(
        (sourceList() ?? []).map(async (source): Promise<SourceDetail> => {
          const [parsed, chunks] = await Promise.all([
            api.tov.parsedPages(currentClientId, source.id),
            api.tov.chunks(currentClientId, source.id),
          ]);
          return {
            sourceId: source.id,
            parsedPages: parsed.pages.length,
            chunks: chunks.chunks.length,
          };
        }),
      );
    },
    enabled: clientId() !== undefined && sourceList() !== null,
    // The query key is the source ids, so page/chunk counts growing mid-parse
    // would never refetch on their own. Slower than the source-list poll: this
    // one fans out two requests per source.
    refetchInterval: () =>
      isCascadeActive(sourceList() ?? []) ? SOURCE_DETAIL_POLL_MS : false,
  }));
  const sourceDetails = () => sourceDetailsQuery.data ?? null;

  const sourceMap = createMemo(
    () => new Map((sourceList() ?? []).map((source) => [source.id, source])),
  );
  const detailMap = createMemo(
    () =>
      new Map<string, SourceDetail>(
        (sourceDetails() ?? []).map((detail: SourceDetail) => [
          detail.sourceId,
          detail,
        ]),
      ),
  );
  const trust = createMemo(() =>
    sourceTrust(
      context.data()?.site ?? null,
      sourceList() ?? [],
      version() ?? undefined,
    ),
  );
  const cards = createMemo(() => {
    const loaded = version();
    if (!loaded) {
      return [] as GuidelineCard[];
    }
    return guidelineCards(loaded, sourceMap(), trust());
  });
  const counts = createMemo(() => {
    const loaded = version();
    return loaded ? definitionCounts(loaded, cards()) : emptyCounts();
  });
  const linkedEvidenceCount = createMemo(() =>
    cards()
      .filter((item) => item.sourceState === TOV_SOURCE_STATE.LINKED)
      .reduce((count, item) => count + item.evidence.length, 0),
  );
  const unestablishedEvidenceCount = createMemo(() =>
    cards()
      .filter((item) => item.sourceState === TOV_SOURCE_STATE.NOT_ESTABLISHED)
      .reduce((count, item) => count + item.evidence.length, 0),
  );
  const traceability = createMemo(() =>
    traceabilityState(version() ?? undefined, cards()),
  );
  const sourceCoverage = createMemo(() =>
    sourceCoverageState(
      sourceList() ?? undefined,
      sourceDetails() ?? undefined,
    ),
  );
  const populatedCoverage = createMemo(() =>
    coverageRows(counts()).filter((row) => row.value > 0),
  );
  const quietEmptyCoverage = createMemo(() =>
    coverageRows(counts()).filter((row) => row.value === 0),
  );

  // The one evidence-drawer opener, bound to the active profile/version; every
  // tab passes the same closure instead of pasting it inline.
  const inspectEvidence =
    (profile: TovProfileResponse, loaded: TovVersionResponse) =>
    (item: GuidelineCard, link: TovDefinitionEvidenceResponse) =>
      setSelectedEvidence({
        item,
        link,
        source: sourceMap().get(link.evidence.sourceId) ?? null,
        profile,
        version: loaded,
      });

  createEffect(() => {
    const selected = selectedEvidence();
    if (!selected) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSelectedEvidence(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    onCleanup(() => window.removeEventListener("keydown", onKeyDown));
  });
  return {
    context,
    clientId,
    activeTab,
    setActiveTab,
    selectedEvidence,
    setSelectedEvidence,
    activeProfile,
    version,
    sourceList,
    hasActiveVersion,
    tovVersionId,
    mapAttempt,
    detailMap,
    trust,
    cards,
    counts,
    linkedEvidenceCount,
    unestablishedEvidenceCount,
    traceability,
    sourceCoverage,
    populatedCoverage,
    quietEmptyCoverage,
    inspectEvidence,
  };
}
